import { useCallback } from 'react';
import { ToastAlert, useToast } from '@frameio/vapor';
import { useDatadogLogger } from '../../shared/hooks/useDatadogLogger';
import { useCreateDeferredPlaceholder } from '../../shared/hooks/useDeferredTransferAssets';
import { ensureFileNamesHaveExtensions } from '../../shared/utils/ensureFileNamesHaveExtensions';
import {
  useAddExportProgressItem,
  useSetDeferredPlaceholder,
} from '../contexts/ExportProgressContext';
import type { ExportSequenceMutation } from '../gql';
import { useExportSequenceMutation } from '../gql';
import type { ExportProgressItem } from '../types/exportProgressTypes';
import TEXT from './generated/TEXT/useExportAndUploadSequence';

TEXT.DEFINE({
  /** A generic error message shown when exporting a sequence fails */
  exportErrorMsg: 'Error exporting sequence',
});

/**
 * @description
 * Hook for managing the export and upload of sequences from Adobe Premiere Pro.
 * Handles the entire lifecycle of an export job, from initiation to completion,
 * including encoding progress, upload progress, and state management.
 *
 * - The process starts the encoding process and sends progress updates via the
 *   `useSequenceEventsSubscription`.
 * - `SequenceEncodeProgressPayload` events advance the item's `progress`
 *   through the encoding phase (weighted by `ENCODING_WEIGHT`).
 * - On `SequenceEncodeCompletePayload`, the user interface creates transfer
 *   jobs via `handleCreateTransferJob`, which prepares the file for upload.
 */
export const useExportAndUploadSequence = () => {
  const { addToast } = useToast();
  const [exportSequenceMutation] = useExportSequenceMutation({
    context: {
      resolveWithUXP: true,
    },
  });

  const addExportProgressItem = useAddExportProgressItem();
  const setDeferredPlaceholder = useSetDeferredPlaceholder();
  const createDeferredPlaceholder = useCreateDeferredPlaceholder();
  const datadogLogger = useDatadogLogger();

  const exportAndUploadSequence = useCallback(
    async ({
      presetId,
      fullSequence,
      fileName,
      sequenceId,
      filePath: exportFilePath,
      folderId,
      onComplete,
    }: {
      presetId: string;
      fullSequence: boolean;
      fileName: string;
      sequenceId: string;
      filePath?: string;
      folderId: string;
      onComplete?: (exportProgressItem: ExportProgressItem) => void;
    }): Promise<boolean> => {
      if (!presetId) {
        return false;
      }

      let exportData: ExportSequenceMutation | null | undefined;

      try {
        const result = await exportSequenceMutation({
          variables: {
            fileName,
            presetId,
            fullSequence,
            filePath: exportFilePath,
          },
        });
        exportData = result.data;
      } catch (error) {
        addToast(<ToastAlert title={TEXT.exportErrorMsg} variant="error" />, {
          duration: 1000,
        });
        datadogLogger.info('integrations-encode-failed', {
          uxpUploads: { source: 'PrUXP' },
        });
        // eslint-disable-next-line no-console
        console.error('Error exporting sequence:', error);
        return false;
      }

      if (
        exportData?.exportSequence?.batchItemId &&
        exportData?.exportSequence?.filePath
      ) {
        const { batchItemId, filePath } = exportData.exportSequence;

        addExportProgressItem(batchItemId, {
          id: batchItemId,
          fileName,
          sequenceId,
          fullSequence,
          folderId,
          onExportCompleteCallback: onComplete,
          createdAt: Date.now(),
        });
        datadogLogger.info('integrations-encode-started', {
          uxpUploads: { source: 'PrUXP' },
        });

        // Not awaited — the dialog shouldn't block on the placeholder round
        // trips. setDeferredPlaceholder handles a late/orphaned resolution.
        const [fileNameWithExtension] = ensureFileNamesHaveExtensions({
          fileNames: [fileName],
          filePaths: [filePath],
        });
        createDeferredPlaceholder({
          fileName: fileNameWithExtension,
          filePath,
          folderAssetId: folderId,
        })
          .then((placeholder) => {
            setDeferredPlaceholder(batchItemId, placeholder);
          })
          .catch((error) => {
            datadogLogger.info('integrations-deferred-placeholder-failed', {
              uxpUploads: {
                source: 'PrUXP',
                message: error instanceof Error ? error.message : String(error),
              },
            });
          });

        return true;
      }

      return false;
    },
    [
      addExportProgressItem,
      setDeferredPlaceholder,
      addToast,
      createDeferredPlaceholder,
      exportSequenceMutation,
      datadogLogger,
    ]
  );

  return exportAndUploadSequence;
};
